import React from 'react'
import { useLDFlagsWithDevOverrides } from '@hooks/useLaunchDarkly'
import { configs } from '@configs'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { brandDisplayName as brandName } from '@typescript'
import { preloadCriticalFragments } from '@utils/aemApi'
import { log } from '@utils/Logger'
import { withBase } from '@utils/basePath'

/** Inventory price unit shown to the member for the current site. EBAC and WorldMark members
 * see "credits"; Club Wyndham and Sports Illustrated members see "points". The same Club Wyndham
 * resort can appear on multiple sites, so the unit follows the site, not the resort. */
export type MkSitePriceUnit = 'credits' | 'points'

export interface MkSiteContextType {
  authBrand: string
  themeBrand: string
  /** Currency unit used for inventory pricing on this site (e.g. "1,234 total credits"). */
  priceUnit: MkSitePriceUnit
  site?: string
  iconSet: 'far' | 'fat' | 'fal' | 'fas' | 'fab' | 'fad' | 'fadl' | 'fadr' | 'fadt' | 'fak' | 'fakd' | 'fasdl' | 'fasdr' | 'fasds' | 'fasdt' | 'fasl' | 'fasr' | 'fass' | 'fast'
  headerLogo?: {
    src: string
    alt: string
  }
  headerLogoDark?: {
    src: string
    alt: string
  }
  headerLogoWhite?: {
    src: string
    alt: string
  }
  [key: string]: any
}

const CREDITS_THEME_BRANDS = new Set<string>(['ebac', 'wm'])

const themeBrandToPriceUnit = (themeBrand: string): MkSitePriceUnit =>
  CREDITS_THEME_BRANDS.has((themeBrand || '').toLowerCase().trim()) ? 'credits' : 'points'

export const MkSiteContext = React.createContext<MkSiteContextType>({} as MkSiteContextType)
export const useSiteContext = () => React.useContext(MkSiteContext)

const determineBrands = (provided?: {
  authBrand?: string
  themeBrand?: string
}): Pick<MkSiteContextType, 'authBrand' | 'themeBrand' | 'priceUnit' | 'site' | 'iconSet' | 'headerLogo' | 'headerLogoDark' | 'headerLogoWhite'> =>
  (() => {
    const storedThemeBrand = DataStore.getItem<string>(EMkStorageKeys.MK_THEME_BRAND) || ''
    const themeBrand = provided?.themeBrand || storedThemeBrand || configs.mk.defaultThemeBrand
    const authBrand = provided?.authBrand || configs.mk.defaultAuthBrand || themeBrand

    log.info(`Using themeBrand: ${themeBrand}, authBrand: ${authBrand}`)

    const site = brandName(themeBrand)
    return {
      authBrand,
      themeBrand,
      priceUnit: themeBrandToPriceUnit(themeBrand),
      site,
      iconSet: themeBrand === 'sir' ? 'fal' : 'far',
      headerLogo: { src: withBase(`/content/images/logos/${themeBrand}.svg`), alt: `${site} Logo` },
      headerLogoDark: { src: withBase(`/content/images/logos/${themeBrand}-logo-dark.svg`), alt: site },
      headerLogoWhite: { src: withBase(`/content/images/logos/${themeBrand}-logo-white.svg`), alt: site },
    }
  })()

export const MkSiteProvider: React.FC<{
  value?: Partial<MkSiteContextType>
  children: React.ReactNode
}> = ({ value = {}, children }) => {
  const initial = determineBrands({ authBrand: value.authBrand, themeBrand: value.themeBrand })
  const [contextValue, setContextValue] = React.useState<MkSiteContextType>({
    ...initial,
    ...value,
  })

  const updateContext = React.useCallback((updates: Partial<MkSiteContextType>) => {
    setContextValue((prev) => ({ ...prev, ...updates }))
  }, [])

  const saveSelectedBrand = React.useCallback((selectedBrand: string) => {
    // Save theme brand selection for visuals; keep authBrand unchanged
    DataStore.setItem(EMkStorageKeys.MK_THEME_BRAND, selectedBrand)
    DataStore.removeItem(EMkStorageKeys.MAKO_RESORTS)
    updateContext({
      ...determineBrands({ themeBrand: selectedBrand }),
    })
  }, [])

  const { switchAemFragments } = useLDFlagsWithDevOverrides<any>()
  const lastPreloadedBrandRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    const currentThemeBrand = contextValue.themeBrand || ''
    const storedThemeBrand = DataStore.getItem<string>(EMkStorageKeys.MK_THEME_BRAND) || ''
    if (!storedThemeBrand && currentThemeBrand) DataStore.setItem(EMkStorageKeys.MK_THEME_BRAND, currentThemeBrand)

    if (!currentThemeBrand || !switchAemFragments || lastPreloadedBrandRef.current === currentThemeBrand) return

    lastPreloadedBrandRef.current = currentThemeBrand
    preloadCriticalFragments(currentThemeBrand).catch((error) => console.warn('Failed to preload critical fragments:', error))
  }, [contextValue.themeBrand, switchAemFragments])

  const memoizedValue = React.useMemo(() => ({ ...contextValue, updateContext, saveSelectedBrand }), [contextValue, saveSelectedBrand])

  return <MkSiteContext.Provider value={memoizedValue}>{children}</MkSiteContext.Provider>
}
